import { useState, useEffect, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import "./App.css";
import "./styles/global.css";
import Login from "./components/Login";
import SiteSelector from "./components/SiteSelector";
import Dashboard from "./components/Dashboard";
import { getToken, removeToken, isTokenExpired } from "./api/auth";
import { getEndpoint } from "./api/apiConfig";
import { isAdminUser } from "./components/siteAccessUtils";

function App() {
  const [loggedIn, setLoggedIn] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [userType, setUserType] = useState("");
  const [authenticatedUser, setAuthenticatedUser] = useState(null);
  const [selectedSite, setSelectedSite] = useState(null);
  const isAdmin = isAdminUser(userType);
  const [loading, setLoading] = useState(true);

  // Debug state changes
  useEffect(() => {
    console.log("=== APP STATE DEBUG ===");
    console.log("loggedIn:", loggedIn);
    console.log("userEmail:", userEmail);
    console.log("userType:", userType);
    console.log("selectedSite:", selectedSite);
    console.log("Should show: ", !loggedIn ? "LOGIN" : !selectedSite ? "SITESELECTOR" : "DASHBOARD");
    console.log("========================");
  }, [loggedIn, userEmail, userType, selectedSite]);
  useEffect(() => {
    const restoreSession = async () => {
      try {
        // Check if token is expired
        if (isTokenExpired()) {
          console.log("Token has expired - clearing session and redirecting to login");
          removeToken();
          sessionStorage.removeItem('tracker_user_email');
          sessionStorage.removeItem('tracker_usertype');
          sessionStorage.removeItem('tracker_selected_site');
          localStorage.removeItem('tracker_user_email');
          localStorage.removeItem('tracker_selected_site');
          setLoggedIn(false);
          setUserEmail("");
          setUserType("");
          setSelectedSite(null);
          setLoading(false);
          return;
        }
        
        const token = getToken();
        
        if (token) {
          // Token exists in storage - restore the logged-in state
          const storedEmail = sessionStorage.getItem('tracker_user_email');
          const storedUserType = sessionStorage.getItem('tracker_usertype');
          const storedSiteJson = sessionStorage.getItem('tracker_selected_site');
          
          console.log("Token found in storage - restoring session");
          console.log("Stored email:", storedEmail);
          console.log("Stored site:", storedSiteJson);
          
          if (storedEmail) {
            setUserEmail(storedEmail);
          }
          if (storedUserType) {
            setUserType(storedUserType);
          }

          const storedUserData = sessionStorage.getItem('tracker_user_data');
          if (storedUserData) {
            try {
              setAuthenticatedUser(JSON.parse(storedUserData));
            } catch (e) {
              console.error("Error parsing stored user data:", e);
              sessionStorage.removeItem('tracker_user_data');
            }
          }
          
          // IMPORTANT: Restore selectedSite if it exists in storage
          if (storedSiteJson) {
            try {
              const site = JSON.parse(storedSiteJson);
              console.log("Restoring site from storage:", site);
              setSelectedSite(site);
            } catch (e) {
              console.error("Error parsing stored site:", e);
              sessionStorage.removeItem('tracker_selected_site');
              setSelectedSite(null);
            }
          } else {
            console.log("No site in storage - user will see site selector");
            setSelectedSite(null);
          }
          
          // Mark as logged in
          setLoggedIn(true);
        } else {
          console.log("No token found - user not logged in");
          sessionStorage.removeItem('tracker_user_email');
          sessionStorage.removeItem('tracker_usertype');
          sessionStorage.removeItem('tracker_selected_site');
          setLoggedIn(false);
          setSelectedSite(null);
        }
      } catch (error) {
        console.error("Error restoring session:", error);
        setLoggedIn(false);
      } finally {
        setLoading(false);
      }
    };

    restoreSession();
  }, []);

  const handleLogout = useCallback(() => {
    console.log("Logging out - clearing all data");
    removeToken();
    sessionStorage.removeItem('tracker_user_email');
    sessionStorage.removeItem('tracker_usertype');
    sessionStorage.removeItem('tracker_user_data');
    sessionStorage.removeItem('tracker_selected_site');
    localStorage.removeItem('tracker_user_email');
    localStorage.removeItem('tracker_user_data');
    localStorage.removeItem('tracker_selected_site');
    setLoggedIn(false);
    setUserEmail("");
    setUserType("");
    setAuthenticatedUser(null);
    setSelectedSite(null);
    window.location.href = '/login';
  }, []);

  // Check for token expiration periodically (every minute)
  useEffect(() => {
    if (!loggedIn) return; // Skip if user is not logged in

    const checkTokenExpiration = () => {
      if (isTokenExpired()) {
        console.log("Token expired due to inactivity - logging out user");
        handleLogout();
      }
    };

    // Check every minute (60000 ms)
    const intervalId = setInterval(checkTokenExpiration, 60000);

    return () => clearInterval(intervalId);
  }, [loggedIn, handleLogout]);

  return (
    <Router>
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', fontSize: 18, color: 'white', fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
          <div>Loading...</div>
        </div>
      ) : (
        <Routes>
          {/* Login Route */}
          <Route
            path="/login"
            element={
              loggedIn ? (
                <Navigate to={selectedSite ? "/dashboard" : "/sites"} replace />
              ) : (
                <Login onLogin={(email, userTypeVal, userData) => {
                  console.log("=== LOGIN CALLBACK START ===");
                  console.log("email:", email, "userType:", userTypeVal, "userData:", userData);
                  sessionStorage.setItem('tracker_user_email', email);
                  sessionStorage.setItem('tracker_usertype', userTypeVal);
                  if (userData) {
                    sessionStorage.setItem('tracker_user_data', JSON.stringify(userData));
                  } else {
                    sessionStorage.removeItem('tracker_user_data');
                  }
                  sessionStorage.removeItem('tracker_selected_site');
                  localStorage.removeItem('tracker_selected_site');
                  setUserEmail(email);
                  setUserType(userTypeVal);
                  setAuthenticatedUser(userData || null);
                  setSelectedSite(null);
                  setLoggedIn(true);
                  console.log("=== LOGIN CALLBACK END ===");
                }} />
              )
            }
          />

          {/* Site Selector Route */}
          <Route
            path="/sites"
            element={
              !loggedIn ? (
                <Navigate to="/login" replace />
              ) : (
                <SiteSelector 
                  onSelectSite={(site) => {
                    console.log("Site selected:", site);
                    setSelectedSite(site);
                    sessionStorage.setItem('tracker_selected_site', JSON.stringify(site));
                    window.location.href = '/dashboard';
                  }} 
                  userEmail={userEmail}
                  userType={userType}
                  authUser={authenticatedUser}
                  onLogout={handleLogout}
                />
              )
            }
          />

          {/* Dashboard Route */}
          <Route
            path="/dashboard"
            element={
              !loggedIn ? (
                <Navigate to="/login" replace />
              ) : !selectedSite ? (
                <Navigate to="/sites" replace />
              ) : (
                <Dashboard 
                  username={userEmail} 
                  userType={userType} 
                  authUser={authenticatedUser}
                  onLogout={handleLogout}
                  onChangeSite={() => {
                    setSelectedSite(null);
                    sessionStorage.removeItem('tracker_selected_site');
                    localStorage.removeItem('tracker_selected_site');
                    window.location.href = '/sites';
                  }}
                  selectedSite={selectedSite}
                />
              )
            }
          />

          {/* Settings Route */}
          <Route
            path="/settings"
            element={
              !loggedIn ? (
                <Navigate to="/login" replace />
              ) : !isAdmin ? (
                <Navigate to="/dashboard" replace />
              ) : !selectedSite ? (
                <Navigate to="/sites" replace />
              ) : (
                <Dashboard 
                  username={userEmail} 
                  userType={userType} 
                  authUser={authenticatedUser}
                  onLogout={handleLogout}
                  onChangeSite={() => {
                    setSelectedSite(null);
                    sessionStorage.removeItem('tracker_selected_site');
                    localStorage.removeItem('tracker_selected_site');
                    window.location.href = '/sites';
                  }}
                  selectedSite={selectedSite}
                  initialActiveTab="settings"
                />
              )
            }
          />

          {/* Root Route - Redirect based on state */}
          <Route
            path="/"
            element={
              !loggedIn ? (
                <Navigate to="/login" replace />
              ) : !selectedSite ? (
                <Navigate to="/sites" replace />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />

          {/* Catch all - redirect to root */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      )}
    </Router>
  );
}

export default App;
